import { inject, Injectable } from '@angular/core';
import { CandidateService } from 'src/app/recruiting/locums-margin-calculator/services/candidate.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { CandidateDetailsStore } from '../candidate-details/candidate-details.store';
import { tapResponse } from '@ngrx/operators';
import { debounceTime, map, of, switchMap, withLatestFrom, take } from 'rxjs';
import { initialCandidateDetailsSlice } from '../candidate-details/candidate-details.slice';
import { CandidateDetails } from '../../models/candidate-details.model';
import { EventBusService, EventPayload } from 'src/app/shared/services/event-bus.service';
import { LocumsMarginCalculatorEvents } from 'src/app/recruiting/locums-margin-calculator/store/enums/locums-margin-calculator-events.enum';
import { CandidateSearchService } from 'src/app/search-all-candidates/candidate-search.service';
import { CandidateSearchStore } from 'src/app/recruiting/locums-margin-calculator/store/candidate-search/candidate-search.store';
import { FetchArgs } from '@aya/controls/aya-table';
import { CandidateSearch } from 'src/app/recruiting/locums-margin-calculator/models/candidate-search.model';
import { RecipientSearch } from '../../models/recipient-search.model';
import { CandidateSearchFilter } from 'src/app/search-all-candidates/models/candidate-search-filter.model';
import { LocumsMarginCalculatorService } from 'src/app/recruiting/locums-margin-calculator/services/locums-margin-calculator.service';
import { MarginExpenseTypesAttributes } from 'src/app/recruiting/locums-margin-calculator/enums/margin-expense-types-attributes.enum';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';

@Injectable({ providedIn: 'root' })
export class CandidateEffects {
    private readonly candidateDetailsStore = inject(CandidateDetailsStore);
    private readonly candidateSearchStore = inject(CandidateSearchStore);
    private readonly candidateSearchService = inject(CandidateSearchService);
    private readonly candidateService = inject(CandidateService);
    private readonly locumsMarginCalculatorService = inject(LocumsMarginCalculatorService);
    private readonly eventBus = inject(EventBusService);
    private readonly _ldFeatureManager = inject(LDFeatureManager);

    private readonly enableSpecialtyPersistenceLocums1099$ = this._ldFeatureManager.isEnabled(
        FeatureFlag.EnableSpecialtyPersistenceLocums1099
    );

    constructor() {
        this.listenToCandidateSearchedEvent();
        this.listenToRecipientCandidateSearchedEvent();
        this.listenToLoadMarginDetailsEvent();
    }

    readonly getCandidateDetails = rxMethod<number>((query$) =>
        query$.pipe(
            withLatestFrom(this.enableSpecialtyPersistenceLocums1099$),
            switchMap(([candidateId, featureFlagEnabled]) =>
                this.candidateService.getCandidateDetails(candidateId).pipe(
                    tapResponse({
                        next: (data: CandidateDetails) => {
                            data = {
                                ...data,
                                candidateExpertiseId: !featureFlagEnabled
                                    ? (data.specialties?.[0]?.candidateExpertiseId ?? 0)
                                    : 0
                            };
                            this.candidateDetailsStore.updateCandidateDetailsFromData({
                                ...data
                            });
                            this.eventBus.emit({
                                type: LocumsMarginCalculatorEvents.CANDIDATE_DETAILS_LOADED,
                                payload: data
                            });
                        },
                        error: () =>
                            this.candidateDetailsStore.updateCandidateDetailsFromData({
                                ...initialCandidateDetailsSlice
                            })
                    })
                )
            )
        )
    );

    readonly getDuplicateMarginId = rxMethod<{ candidateId: number; jobId: number }>((query$) =>
        query$.pipe(
            switchMap(({ candidateId, jobId }) =>
                this.locumsMarginCalculatorService.getDuplicateMarginId(candidateId, jobId).pipe(
                    tapResponse({
                        next: (marginId: number) => {
                            this.candidateSearchStore.setMarginId(marginId);
                        },
                        error: () => {
                            this.candidateSearchStore.setMarginId(null);
                        }
                    })
                )
            )
        )
    );

    readonly searchCandidates = rxMethod<string>((query$) =>
        query$.pipe(
            debounceTime(250),
            switchMap((searchText) => {
                const fetchArgs = this.createSearchFetchArgs(searchText, (text) =>
                    this.candidateSearchService.createFilter(text)
                );

                if (!fetchArgs) {
                    this.candidateSearchStore.setCandidates([]);
                    return of([] as CandidateSearch[]);
                }

                return this.candidateService.rapidSearchLocumsCandidates(fetchArgs).pipe(
                    map((result) => {
                        result.forEach((x) => {
                            x.fullName = `${x.firstName} ${x.lastName}`;
                            x.recruiterFullName = `${x.travelRecruiterFirstName} ${x.travelRecruiterLastName}`?.trim();
                        });
                        this.candidateSearchStore.setCandidates(result);
                        return result;
                    })
                );
            })
        )
    );

    readonly getExpertisesByProfessionId = rxMethod<number>((query$) =>
        query$.pipe(
            switchMap((professionId) =>
                this.candidateService.getExpertisesByProfessionId(professionId).pipe(
                    tapResponse({
                        next: (professionExpertises) => {
                            this.candidateDetailsStore.updateCandidateDetailsFromData({ professionExpertises });
                        },
                        error: () => {
                            this.candidateDetailsStore.updateCandidateDetailsFromData({ professionExpertises: [] });
                        }
                    })
                )
            )
        )
    );

    readonly searchCandidatesByNameOrId = rxMethod<string>((query$) =>
        query$.pipe(
            debounceTime(500),
            switchMap((searchText) => {
                const fetchArgs = this.createSearchFetchArgs(searchText, (text) =>
                    this.candidateSearchService.createRecipientFilter(text)
                );

                if (!fetchArgs) {
                    this.candidateSearchStore.setRecipients([]);
                    return of([] as RecipientSearch[]);
                }

                return this.candidateService.searchCandidateByNameOrId(fetchArgs).pipe(
                    map((result) => {
                        result.forEach((x) => {
                            x.fullName = `${x.firstName} ${x.lastName}`;
                        });
                        this.candidateSearchStore.setRecipients(result);
                        return result;
                    })
                );
            })
        )
    );

    readonly setSelectedRecipient = rxMethod<string>((query$) =>
        query$.pipe(
            switchMap((searchText) => {
                const fetchArgs = this.createSearchFetchArgs(searchText, (text) =>
                    this.candidateSearchService.createRecipientFilter(text)
                );

                if (!fetchArgs) {
                    this.candidateSearchStore.setSelectedRecipient(null);
                    return of(null);
                }

                return this.candidateService.searchCandidateByNameOrId(fetchArgs).pipe(
                    map((result) => {
                        const firstResult = result.length > 0 ? result[0] : null;
                        if (firstResult) {
                            firstResult.fullName = `${firstResult.firstName} ${firstResult.lastName}`;
                        }
                        this.candidateSearchStore.setSelectedRecipient(firstResult);
                        return firstResult;
                    })
                );
            })
        )
    );

    private listenToCandidateSearchedEvent() {
        this.eventBus.on(LocumsMarginCalculatorEvents.CANDIDATE_SEARCHED).subscribe((event: EventPayload) => {
            this.searchCandidates(event.payload);
        });

        this.eventBus.on(LocumsMarginCalculatorEvents.CANDIDATE_SELECTED).subscribe((event: EventPayload) => {
            this.getDuplicateMarginId({
                candidateId: event.payload.candidate.id as number,
                jobId: event.payload.jobId as number
            });
        });
    }

    private listenToRecipientCandidateSearchedEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.REFERRAL_BONUS_RECIPIENT_SEARCHED)
            .subscribe((event: EventPayload) => {
                this.searchCandidatesByNameOrId(event.payload.recipient);
            });
    }

    private listenToLoadMarginDetailsEvent() {
        // for existing margin
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_LOAD_MARGIN_DETAILS)
            .pipe(withLatestFrom(this.enableSpecialtyPersistenceLocums1099$), take(1))
            .subscribe(([event, featureFlagEnabled]) => {
                if (featureFlagEnabled) {
                    const candidateDetails = event.payload.marginDetails.candidateDetails as CandidateDetails;
                    if (candidateDetails) {
                        this.getExpertisesByProfessionId(candidateDetails.professionId);
                    }
                }

                const recipientId = event.payload.marginDetails.marginExpenses.find(
                    (e) => e.marginExpenseAttributeId === MarginExpenseTypesAttributes.Recipient
                )?.value;

                if (!recipientId) {
                    this.candidateSearchStore.clearSelectedRecipient();
                    return;
                }

                this.setSelectedRecipient(recipientId);
            });

        // for new margin
        this.eventBus
            .on(LocumsMarginCalculatorEvents.CANDIDATE_DETAILS_LOADED)
            .pipe(withLatestFrom(this.enableSpecialtyPersistenceLocums1099$), take(1))
            .subscribe(([event, featureFlagEnabled]) => {
                if (featureFlagEnabled) {
                    const candidateDetails = event.payload as CandidateDetails;
                    this.getExpertisesByProfessionId(candidateDetails.professionId);
                }
            });
    }

    private createSearchFetchArgs(
        searchText: string,
        createFilterFn: (searchText: string) => CandidateSearchFilter
    ): FetchArgs | null {
        const searchParams = {
            textFilterLength: 2,
            candidateSearchResultSize: 10,
            defaultSort: { field: 'firstName', dir: 'asc' }
        };

        if (searchText?.length < searchParams.textFilterLength) {
            return null;
        }

        const filter = createFilterFn(searchText);

        return {
            filter,
            sort: searchParams.defaultSort,
            skip: 0,
            pageSize: searchParams.candidateSearchResultSize
        } as FetchArgs;
    }
}
